/* eslint-disable max-lines */
/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from "vuex";
import individualDiagnose from "../../../../../share/util/individualDiagnose";
import atUtil from "../../../../pages/individualActiveTest/individualActiveTestUtil";
import {
  ERR_KEY,
  ERR_TYPE,
  RES_TYPE,
  MNTR_STATUS,
  FLOW,
  FLOW_ERROR,
  AT_EXE_TYPE
} from "../../../../pages/individualActiveTest/individualActiveTestConstants.js";
import config from "../../../../../share/config";
import session from "../../../../../share/session";
import loading from "../../../../../share/components/vue/ProcessingIcon";

const MENUS = {
  ATID: "active_test_id",
  REQID: "requirement_id",
  SID22: "sid0x22",
  RID: "rid",
};
const REG_STATUS_ON = "01";
const ON_OFF_STATUS_ON = "01";
const AT_EXE = {
  STATUS: "status",
  MNTR_STATUS: "monitoring_status",
  USER_DATA: "user_data",
  RES_TYPE: "res_type",
  VAL: "value",
  REQUEST_RESULT: "request_result",
};
const BTN_STYLE_INACTIVE = {
  ["short-btn"]: true,
  ["inactive-btn"]: true
};
const BTN_STYLE_ACTIVE = {
  ["short-btn"]: true,
  ["inactive-btn"]: false
};

export default {
  name: "requirementSP4",
  data() {
    return {
      /** 状態遷移 */
      flow: FLOW.INIT,
      // /** 変換情報(SP4では参照不要) */
      // conversionInfoList: [], //(doStart, doStop, エラーの停止処理以外で変更してはならない)
      /** 停止中フラグ */
      isStopping: false,
      /** ボタンデータ */
      btns: [],
      /** 選択中のボタン */
      selectedBtn: null,
      /** AT文言 */
      message: "",
      displayProcessing: false,
      // サインアウト
      signout: false,
    };
  },

  props: {
    // 選択されたAT項目
    selectedAtFunc: { type: Object, required: true },
    // 選択項目名称i18nキー
    i18nMenuTitleKey: { type: String, default: "", required: false },
    // 駆動中フラグ、親画面の画面遷移判定で使用
    isDriving: { type: Boolean, required: true },
    // DM駆動中フラグ
    inExecution: { type: Boolean, required: true },
  },

  components: {
    "processing-view": loading,
  },

  watch: {
    // 言語設定変更時処理
    language: {
      async handler() {
        this.clearAtMsg();
        this.btns = await this.createBtnData();
        if (this.flow === FLOW.STANDBY) {
          this.selectedBtn = null;
        } else {
          this.disableAllItemBtns();
        }
      },
    },
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      netAppId: (state) => state.netAppConnector.pairingAppId,
    }),
    systemId() {
      return individualDiagnose.getCurrentSystemId(this);
    },
    activeTestId() {
      return this.selectedAtFunc[MENUS.ATID];
    },
    requirementId() {
      return this.selectedAtFunc[MENUS.REQID];
    },
    ridList() {
      return this.selectedAtFunc[MENUS.RID];
    },
    checkDidList() {
      // SID22のdidをすべて取得する
      const didList = [];
      this.selectedAtFunc[MENUS.SID22].forEach(item => {
        didList.push(item.regStatus);
        didList.push(item.onOffStatus);
      });
      return didList;
    },
    uiid() {
      return this.selectedAtFunc[MENUS.ATID].slice(0, -2);
    },
    isDriveFlow() {
      return [FLOW.PRE_EXECUTE, FLOW.EXECUTE, FLOW.CHECK_EXE].includes(this.flow);
    },
    driveBtnLabel() {
      return this.isDriving
        ? this.$t("individual.button_data_monitor_stop")
        : this.$t("individual.button_active_test_drive");
    },
    btnDisabled() {
      const disabled = {
        drive: this.getDriveBtnDisabled(),
      };
      return disabled;
    },
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /**
     * AT項目選択ボタンクリック時の処理
     * AT項目選択 → DM項目選択 遷移時に行う処理を記述する
     */
    async btnAtSelectOnClicked() {
      this.displayProcessing = true;
      // データ読み出し実行フラグをfalseに切り替え
      this.$emit("toggle-read-executed", false);
      // ボタンデータの作成
      this.btns = await this.createBtnData();
      // 初期表示処理
      this.displayInit();
      this.selectedBtn = null;
      this.displayProcessing = false;
    },

    /**
     * 初期表示処理
     * flowをSTANDBYへ遷移させ、駆動ボタンの表示を「駆動」に切り替える
     */
    displayInit() {
      // 待機に遷移
      this.flow = FLOW.STANDBY;
      this.$emit("toggle-drive", false);
      this.enableAllItemBtns();
    },

    /** AT文言の削除 */
    clearAtMsg() {
      this.message = "";
    },

    /** 項目ボタン押下時の処理 */
    btnClick(clickedBtn) {
      this.btns.forEach((btn) => {
        if (btn.key === clickedBtn.key) {
          this.selectedBtn = clickedBtn;
          btn.style = BTN_STYLE_ACTIVE;
        } else {
          btn.style = BTN_STYLE_INACTIVE;
        }
      });
      return clickedBtn;
    },

    /** 全項目ボタンの非活性化 */
    disableAllItemBtns() {
      this.btns.forEach((btn) => {
        btn.disabled = true;
      });
    },

    /** 全項目ボタンの活性化 */
    enableAllItemBtns() {
      this.btns.forEach((btn) => {
        btn.disabled = false;
      });
    },

    /** 駆動ボタンの活性・非活性 */
    getDriveBtnDisabled() {
      const res = ![
        FLOW.STANDBY,
        FLOW.EXECUTE,
        FLOW.CHECK_EXE,
      ].includes(this.flow) ||
        !this.selectedBtn ||
        (this.selectedBtn && !this.selectedBtn.isSupport);
      return res;
    },

    /** 駆動ボタン押下時イベント */
    clickDriveBtn() {
      if (this.isDriving) {
        if (!this.selectedBtn.isRegist || !this.selectedBtn.isOnOff) {
          // 未登録表示、OFF状態表示の場合
          this.waitDrive();
        }
        else {
          // 停止
          this.stopDrive();
        }

      } else {
        if (!this.selectedBtn.isSupport) {
          // 無効のため駆動しない
          return;
        }
        else if (!this.selectedBtn.isRegist) {
          // 未登録表示
          this.waitStop(this.$t("individual.label_active_test_message_sp4_unregistered"));
        }
        else if (!this.selectedBtn.isOnOff) {
          // OFF状態表示
          this.waitStop(this.$t("individual.label_active_test_message_sp4_nonfunctional"));
        }
        else {
          // 駆動
          this.startDrive();
        }
      }
    },

    /** 駆動処理せず停止待ちに遷移する */
    waitStop(atMsg = "") {
      this.$emit("toggle-drive", true);
      this.flow = FLOW.EXECUTE;
      this.disableAllItemBtns();
      this.message = atMsg;
    },

    /** 停止処理せず駆動待ちに遷移する */
    waitDrive() {
      this.selectedBtn.style = BTN_STYLE_INACTIVE;
      this.selectedBtn = null;
      this.clearAtMsg();
      this.displayInit();
    },

    /** 駆動ボタン押下処理 */
    async startDrive() {
      this.$emit("toggle-drive", true);
      this.flow = FLOW.PRE_EXECUTE;
      this.disableAllItemBtns();
      this.clearAtMsg();

      // マーカ登録処理(駆動開始時)
      const markInfo = {
        "type": AT_EXE_TYPE.START,
        "info": [
          {
            "param": "ID",
            "value": this.selectedBtn.name
          }
        ]
      };
      await this.$emit("save-active-test-executeMark", markInfo);

      const response = await atUtil.doStandby(this, true);
      if (response) {
        switch (response.data.status) {
          case config.STATUS_DMAT_COMPLETED:
            {
              let checkResponse = true;
              if (response.data.user_data && Array.isArray(response.data.user_data)) {
                for (const userData of response.data.user_data) {
                  /* eslint-disable-next-line max-depth */
                  if (userData.res_type !== RES_TYPE.OK) {
                    checkResponse = false;
                    break;
                  }
                }
              }
              // 正常終了
              if (checkResponse) {
                // AT実行
                await this.doStart();
              }
              else {
                // 共通エラーへ遷移
                this.flow = FLOW.ERR;
                this.doErrorProcess(ERR_KEY.COMMON);
              }
            }
            break;
          default:
            // 共通エラーへ遷移
            this.flow = FLOW.ERR;
            this.doErrorProcess(ERR_KEY.COMMON);
            break;
        }
      }
    },

    /** 停止ボタン押下処理 */
    async stopDrive() {
      this.flow = FLOW.STOP;

      // マーカ登録処理(駆動停止時)
      const markInfo = {
        "type": AT_EXE_TYPE.END
      };
      await this.$emit("save-active-test-executeMark", markInfo);

      const response = await this.doStop();
      if (response) {
        switch (response.data.status) {
          case config.STATUS_DMAT_COMPLETED:
            {
              const result = await this.doTerminate();
              if (result) {
                this.selectedBtn.style = BTN_STYLE_INACTIVE;
                this.selectedBtn = null;
                // 初期表示処理
                this.displayInit();

                // アクティブ文言のクリア判定
                /* eslint-disable-next-line max-depth */
                if (atUtil.isClearMessageOnStop(this.requirementId)) {
                  this.clearAtMsg();
                }
              } else {
                // 共通エラーへ遷移
                this.flow = FLOW.ERR;
                this.doErrorProcess(ERR_KEY.COMMON);
              }
            }
            break;
          default:
            // 共通エラー処理済み
            break;
        }
      }
    },

    /** ボタンに登録するデータのオブジェクトの作成 */
    async createBtnData() {
      const res = [];
      // データ読み出し
      const response = await atUtil.doRead(this, this.checkDidList);
      // レスポンスがとれてないなら異常なので共通エラーとする
      if (
        !response ||
        !response.data ||
        !response.data[AT_EXE.USER_DATA] ||
        !response.data[AT_EXE.USER_DATA].length === 0 ||
        config.STATUS_DMAT_FAILED === response.data[AT_EXE.STATUS]
      ) {
        // 共通エラーへ遷移
        this.flow = FLOW.ERR;
        this.doErrorProcess(ERR_KEY.COMMON);
        return res;
      }

      // データ読み出し実行フラグをtrueに切り替え
      this.$emit("toggle-read-executed", true);

      const readDataList = response.data[AT_EXE.USER_DATA];
      if (readDataList.findIndex((data) => data[AT_EXE.RES_TYPE] !== RES_TYPE.OK) > -1) {
        // 共通エラーへ遷移
        this.flow = FLOW.ERR;
        this.doErrorProcess(ERR_KEY.COMMON);
        return res;
      }
      this.selectedAtFunc[MENUS.SID22].forEach((targetData, index) => {
        // サポートDIDのみ応答あり
        const readRegStatus = readDataList.find((data) => data.id === parseInt(targetData.regStatus.substring(0, 4), 16));
        const readOnOffStatus = readDataList.find((data) => data.id === parseInt(targetData.onOffStatus.substring(0, 4), 16));
        const isSupport = (readRegStatus !== undefined) && (readOnOffStatus !== undefined);
        let isRegist = false;
        let isOnOff = false;
        if (isSupport) {
          isRegist = readRegStatus[AT_EXE.VAL].slice(6) === REG_STATUS_ON;
          isOnOff = readOnOffStatus[AT_EXE.VAL].slice(6) === ON_OFF_STATUS_ON;
        }

        const btnObj = {
          name: this.$t("individual.button_active_test_id") + targetData.id,
          key: index,
          value: null,
          sid: this.selectedAtFunc[MENUS.RID][index],
          disabled: false,
          isSupport: isSupport,
          isRegist: isRegist,
          isOnOff: isOnOff,
          style: BTN_STYLE_INACTIVE,
        };
        res.push(btnObj);
      });

      return res;
    },

    //=================== API =======================
    /**
     * 駆動API通信開始
     */
    async doStart() {
      this.isStopping = false;

      // API実行パラメータ作成
      const token = session.getToken();
      const requestBody = atUtil.genReqBodyStart(
        token.id_token,
        this.selectedAtFunc,
        this.selectedBtn.value,
        this.selectedBtn.key
      );

      // start API実行
      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => {
          this.flow = FLOW.EXECUTE;
          return config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
        },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          if (atUtil.isErrorAbort(getApiResponse.data[AT_EXE.REQUEST_RESULT])) {
            // 開始のポーリングでCCエラー中断のレスポンスは、データモニタでエラーとなった場合のため、通信エラーへ遷移
            // ただしデータモニタ起点で既にエラー処理をおこなっている場合は、そのままポーリング終了
            if (!FLOW_ERROR.includes(this.flow)) {
              this.flow = FLOW.ERR;
              this.doErrorProcess(ERR_KEY.COMMUNICATION);
            }
            return config.INDIVIDUAL_POLLING_RESULT_FINISH;
          }

          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[AT_EXE.USER_DATA]);
          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const isExeOnly =
            getApiResponse.data[AT_EXE.USER_DATA].length === 0 &&
            getApiResponse.data[AT_EXE.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (
            isExeOnly ||
            !(AT_EXE.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[AT_EXE.MNTR_STATUS]
          ) {
            // 実行
            if (
              isExeOnly ||
              monitoringStatusData[AT_EXE.RES_TYPE] === RES_TYPE.OK
            ) {
              // 正常終了
              // 実行監視に遷移
              if (!self.isStopping) {
                self.flow = FLOW.CHECK_EXE;
              }
              result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
            } else {
              // 異常終了
              // 通信エラーへ遷移
              self.flow = FLOW.ERR_COMMUNICATE;
              self.doErrorProcess(ERR_KEY.COMMUNICATION);
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          } else {
            // 実行監視
            if (monitoringStatusData[AT_EXE.RES_TYPE] === RES_TYPE.OK) {
              if (!self.isStopping) {
                self.flow = FLOW.CHECK_EXE;
              }
              const status = atUtil.getMonitoringStatus(monitoringStatusData[AT_EXE.VAL], self.requirementId);
              // AT文言表示
              const atMsg = atUtil.getAtMsg(
                self,
                self.systemId,
                self.requirementId,
                parseInt(status, 16),
                self.isDriveFlow
              );
              this.message = atMsg.replace(/\$USER\$/, this.selectedBtn.name);
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            } else {
              // 異常終了
              // 通信エラーへ遷移
              self.flow = FLOW.ERR_COMMUNICATE;
              self.doErrorProcess(ERR_KEY.COMMUNICATION);
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          }
          if (self.isStopping) {
            // 停止処理が開始されたのでstartのポーリングを終了させる
            result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
          }
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        (getApiResponse, self) => {
          // 共通エラーへ遷移
          self.flow = FLOW.ERR;
          self.doErrorProcess(ERR_KEY.COMMON);
        },
      );

      return response;
    },

    /**
     * 駆動API通信停止
     */
    async doStop() {
      this.isStopping = true;

      // API実行パラメータ作成
      const token = session.getToken();
      const requestBody = atUtil.genReqBodyStop(
        token.id_token,
        this.selectedAtFunc,
        this.selectedBtn.key
      );

      // stop API実行
      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => {
          return config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
        },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[AT_EXE.USER_DATA]);
          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const isStopOnly =
            getApiResponse.data[AT_EXE.USER_DATA].length === 0 &&
            getApiResponse.data[AT_EXE.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (
            isStopOnly ||
            !(AT_EXE.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[AT_EXE.MNTR_STATUS]
          ) {
            // 停止
            if (
              isStopOnly ||
              monitoringStatusData[AT_EXE.RES_TYPE] === RES_TYPE.OK
            ) {
              // 正常終了
            } else {
              // 異常終了
              // 共通エラーへ遷移
              self.flow = FLOW.ERR;
              self.doErrorProcess(ERR_KEY.COMMON);
            }
          } else {
            // SP4では停止監視はなし
          }
          result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        (getApiResponse, self) => {
          // 共通エラーへ遷移
          self.flow = FLOW.ERR;
          self.doErrorProcess(ERR_KEY.COMMON);
        }
      );

      return response;
    },

    /** 正常時のAT終了処理 
     * @async
     * @return {boolean} terminate result - false is faild.
     */
    async doTerminate() {
      const response = await atUtil.doTerminate(this, false);
      if (response) {
        if (response.data.status === config.STATUS_DMAT_COMPLETED) {
          // 正常終了
          if (!response.data.user_data
            || response.data.user_data.length === 0
            || response.data.user_data[0][AT_EXE.RES_TYPE] === RES_TYPE.OK) {

            return true;
          }
        }
      }

      return false;
    },

    /******************** エラー処理 ********************/
    /** 通信エラー画面処理 */
    async communicateError() {
      this.isStopping = true;

      // API実行パラメータ作成
      const idx = this.selectedBtn.key;
      const token = session.getToken();
      const requestBody = atUtil.genReqBodyStop(
        token.id_token,
        this.selectedAtFunc,
        idx,
        ERR_TYPE.COMMUNICATE
      );

      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => { },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[AT_EXE.USER_DATA]);
          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const isStopOnly =
            getApiResponse.data[AT_EXE.USER_DATA].length === 0 &&
            getApiResponse.data[AT_EXE.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (
            isStopOnly ||
            !(AT_EXE.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[AT_EXE.MNTR_STATUS]
          ) {
            // 停止
            if (
              isStopOnly ||
              monitoringStatusData[AT_EXE.RES_TYPE] === RES_TYPE.OK
            ) {
              // 正常終了
              // 通信エラーを表示
              atUtil.showUnexpectedError(
                self,
                ERR_KEY.COMMUNICATION,
                self.requirementId,
                null,
                async () => {
                  // 停止時に他の操作をさせないようにする
                  self.displayProcessing = true;
                  await atUtil.doTerminate(self, true);
                  self.displayInit();
                  self.$emit('at-failure');
                  self.displayProcessing = false;
                }
              );
            } else {
              // 異常終了
              // 共通エラーへ遷移
              self.flow = FLOW.ERR;
              self.doErrorProcess(ERR_KEY.COMMON);
            }
          } else {
            // SP4では停止監視はなし
          }
          result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        () => {
          // 共通エラーへ遷移
          self.flow = FLOW.ERR;
          self.doErrorProcess(ERR_KEY.COMMON);
        }
      );
      return response;
    },

    /** 共通エラー画面処理 */
    commonError(requiredTerminal = true) {
      // 共通エラーを表示
      atUtil.showUnexpectedError(
        this,
        ERR_KEY.COMMON,
        this.requirementId,
        null, // 共通エラーはnull
        async () => {
          if (requiredTerminal) {
            // 停止時に他の操作をさせないようにする
            this.displayProcessing = true;
            await atUtil.doTerminate(this, true);
            this.displayInit();
            this.displayProcessing = false;
          }
          this.$emit('at-failure');
        }
      );
    },

    /** 
     * エラー処理の振り分け
     * @param {*} errKey エラーキー
     */
    async doErrorProcess(errKey) {
      switch (errKey) {
        case ERR_KEY.COMMUNICATION:
          await this.communicateError();
          break;
        case ERR_KEY.COMMON:
        default:
          this.commonError();
          break;
      }
    },

    /** キャンセル処理 */
    async cancelExec() {
      // 駆動中であれば停止する。ただし他のエラー処理を既に実行していれば何もしない
      if (this.isDriveFlow && !FLOW_ERROR.includes(this.flow)) {
        if (!this.selectedBtn.isRegist || !this.selectedBtn.isOnOff) {
          // 未登録表示、OFF状態表示の場合は停止処理をしない
          this.waitDrive();
        } else {
          this.flow = FLOW.STOP;
          await this.doStop();
        }
      }
    },

    setSignout() {
      this.signout = true;
    },
  },

  mounted() {
    // noop
  },
};
